import React, { useEffect, useState } from 'react';
import { Plus as PlusIcon, LoaderCircle } from 'lucide-react';
import { useApiData } from '../../../../../contexts/apiService';
import { getUniqueDropdownOptions } from '../../../../../helper_functions';
import SequenceItem from './sequenceItem';
import './sequenceComponent.css';
import Dropdown from '../../../../common/dropdown/dropdown';

interface SequenceComponentProps {
    panelData: any;
    onEditingStateChange?: (isEditing: boolean) => void;
}

interface PickupData {
    id: number;
    sequence: number;
    producer_id: number;
    lease_id: number;
    lease_name: string;
    tank_name: string;
    ticket_number: string | null;
}

interface DeliveryData {
    id: number;
    sequence: number;
    destination_id: number;
    destination_name: string;
    offload_lane: string;
}

const SequenceComponent: React.FC<SequenceComponentProps> = ({ 
    panelData,
    onEditingStateChange,
}) => {
    const { apiData, handleEditSequence, handleAddRow } = useApiData();
    
    const [loadSequence, setLoadSequence] = useState<PickupData[]>([]);
    const [offloadSequence, setOffloadSequence] = useState<DeliveryData[]>([]);

    const [parentId, setParentId] = useState<number>();
    const [totalVolume, setTotalVolume] = useState<number>();

    const [draggedItemIndexLoad, setDraggedItemIndexLoad] = useState<number | null>(null);
    const [draggedOverIndexLoad, setDraggedOverIndexLoad] = useState<number | null>(null);
    const [draggedItemIndexUnload, setDraggedItemIndexUnload] = useState<number | null>(null);
    const [draggedOverIndexUnload, setDraggedOverIndexUnload] = useState<number | null>(null);

    const [isAnyItemEditing, setIsAnyItemEditing] = useState<boolean>(false);
    const [isEditLoading, setIsEditLoading] = useState<boolean>(false);

    const [editedItemIndexLoad, setEditedItemIndexLoad] = useState<number | null>(-1);
    const [editedItemIndexUnload, setEditedItemIndexUnload] = useState<number | null>(-1);

    const [originDropdownOptions, setOriginDropdownOptions] = useState<{ value: string; label: string }[]>([]);
    const [destinationDropdownOptions, setDestinationDropdownOptions] = useState<{ value: string; label: string }[]>([]);
    const [newOriginLocation, setNewOriginLocation] = useState<string>('');
    const [newDestinationLocation, setNewDestinationLocation] = useState<string>('');

    // Handle edit notifications from child components
    const handleEditStart = (type: string, item_index: number, itemId: number, isEditing: boolean) => {

        if (isEditing) {
            // Close any other editing items and set this one as editing
            setIsAnyItemEditing(true);
            if (type === 'pickup') {
                setEditedItemIndexLoad(item_index)
            } else {
                setEditedItemIndexUnload(item_index)
            }
        } else {
            // Item stopped editing
            setIsAnyItemEditing(false);
            setEditedItemIndexLoad(-1)
            setEditedItemIndexUnload(-1)
        }

        // Notify parent component about editing state
        if (onEditingStateChange) {
            onEditingStateChange(isEditing);
        }
    };

    const handleLoadStart = (isLoading: boolean) => {
        setIsEditLoading(isLoading)
    };

    // Extract and sort pickup data by sequence
    const getOrderedSequence = (data: string): any => {
        if (!data) {
            return [];
        }

        let pickupData;
        try {
            pickupData = typeof data === 'string' 
                ? JSON.parse(data) 
                : data;
        } catch (error) {
            console.error('Error parsing pickup_data:', error);
            return [];
        }

        // Convert to array
        const pickupArray = Array.isArray(pickupData) ? pickupData : [pickupData];

        return pickupArray
            .filter(item => item && typeof item.sequence === 'number')
            .sort((a, b) => a.sequence - b.sequence);
    };

    // Initialize state when panel opens or data changes
    useEffect(() => {
        if (!panelData?.rowData) return;
        
        if (panelData.rowData.pickup_data) {
            const loadSequence = getOrderedSequence(panelData.rowData.pickup_data);
            setLoadSequence(loadSequence);
            setTotalVolume(panelData.rowData.gross_volume);
        } else {
            setLoadSequence([]);
            setTotalVolume(0);
        }

        if (panelData.rowData.delivery_data) {
            const offloadSequence = getOrderedSequence(panelData.rowData.delivery_data);
            setOffloadSequence(offloadSequence);
        } else {
            setOffloadSequence([]);
        }
        
        setParentId(panelData.rowData.id);

    }, [panelData]);
    
    // Drag handlers
    const handleDragStart = (e: React.DragEvent, index: number, sequence: string) => {
        if (isAnyItemEditing) {
            e.preventDefault(); // Prevent dragging while updating
            return;
        }
        if (sequence === 'load') {
            setDraggedItemIndexLoad(index);
        } else {
            setDraggedItemIndexUnload(index);
        }
        e.dataTransfer.effectAllowed = 'move';
        // visual feedback
        e.currentTarget.classList.add('dragging');
    };

    const handleDragEnd = (e: React.DragEvent, sequence: string) => {
        if (sequence === 'load') {
            setDraggedItemIndexLoad(null);
            setDraggedOverIndexLoad(null);
        } else {
            setDraggedItemIndexUnload(null);
            setDraggedOverIndexUnload(null);
        }

        // Remove visual feedback
        e.currentTarget.classList.remove('dragging');
    };

    const handleDragOver = (e: React.DragEvent, index: number, sequence: string) => {
        if (isAnyItemEditing) {
            e.preventDefault();
            return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        
        if (sequence === 'load') {
            if (draggedItemIndexLoad !== null && draggedItemIndexLoad !== index) {
                setDraggedOverIndexLoad(index);
            }
        } else {
            if (draggedItemIndexUnload !== null && draggedItemIndexUnload !== index) {
                setDraggedOverIndexUnload(index);
            }
        }
    };

    const handleDragLeave = (sequence: string) => {
        if (sequence === 'load') {
            setDraggedOverIndexLoad(null);
        } else {
            setDraggedOverIndexUnload(null);
        }
    };
    
    const handleDrop = async (e: React.DragEvent, dropIndex: number, sequence: string) => {
        e.preventDefault();

        if (sequence === 'load') {
            if (draggedItemIndexLoad === null || draggedItemIndexLoad === dropIndex || isAnyItemEditing) {
                setDraggedItemIndexLoad(null);
                setDraggedOverIndexLoad(null);
                console.log('No change in position, not updating.');
                return;
            }
        } else {
            if (draggedItemIndexUnload === null || draggedItemIndexUnload === dropIndex || isAnyItemEditing) {
                setDraggedItemIndexUnload(null);
                setDraggedOverIndexUnload(null);
                console.log('No change in position, not updating.');
                return;
            }       
        }

        // Capture the original sequence before making changes
        let originalSequence: PickupData[] | DeliveryData[];
        let newSequence: PickupData[] | DeliveryData[];
        let setSequenceFunction: (sequence: PickupData[] | DeliveryData[]) => void;
        let tableName: string;
        let dataKey: string;

        if (sequence === 'load') {
            originalSequence = [...loadSequence];
            newSequence = [...loadSequence];
            setSequenceFunction = (seq: PickupData[] | DeliveryData[]) => setLoadSequence(seq as PickupData[]);
            tableName = 'crude_schedule_pickup';
            dataKey = 'sequence_data';
        } else {
            originalSequence = [...offloadSequence];
            newSequence = [...offloadSequence];
            setSequenceFunction = (seq: PickupData[] | DeliveryData[]) => setOffloadSequence(seq as DeliveryData[]);
            tableName = 'crude_schedule_delivery';
            dataKey = 'sequence_data';
        }

        let draggedIndex: number;
        // Get the dragged item index
        if (sequence === 'load' && draggedItemIndexLoad !== null) {
            draggedIndex = draggedItemIndexLoad;
        } else if (sequence === 'offload' && draggedItemIndexUnload !== null) {
            draggedIndex = draggedItemIndexUnload;
        } else {
            console.error('Dragged item index is null, cannot proceed with drop.');
            return;
        }

        // Remove from old position and insert at new position with proper typing
        if (sequence === 'load') {
            const pickupSequence = [...newSequence] as PickupData[];
            
            // Swap lease_id and tank_name between the two items
            const tempLeaseId = pickupSequence[draggedIndex].lease_id;
            const tempTankName = pickupSequence[draggedIndex].tank_name;
            
            pickupSequence[draggedIndex].lease_id = pickupSequence[dropIndex].lease_id;
            pickupSequence[draggedIndex].tank_name = pickupSequence[dropIndex].tank_name;
            
            pickupSequence[dropIndex].lease_id = tempLeaseId;
            pickupSequence[dropIndex].tank_name = tempTankName;
            
            setLoadSequence(pickupSequence);
            setIsAnyItemEditing(true);
            
            // Only send the two items that were swapped
            const editData = {
                parent_id: parentId,
                [dataKey]: [
                    {
                        id: pickupSequence[draggedIndex].id,
                        sequence: pickupSequence[draggedIndex].sequence,
                        location_id: pickupSequence[draggedIndex].lease_id,
                        secondary: pickupSequence[draggedIndex].tank_name,
                    },
                    {
                        id: pickupSequence[dropIndex].id,
                        sequence: pickupSequence[dropIndex].sequence,
                        location_id: pickupSequence[dropIndex].lease_id,
                        secondary: pickupSequence[dropIndex].tank_name,
                    }
                ]
            };

            try {
                setIsEditLoading(true);
                const success = await handleEditSequence(tableName, editData);

                if (!success) {
                    console.error(`API call failed for ${sequence} sequence, reverting to original sequence`);
                    setLoadSequence(originalSequence as PickupData[]);
                    alert(`Failed to update ${sequence} sequence. Changes have been reverted.`);
                } else {
                    console.log(`${sequence} sequence updated successfully`);
                }
            } catch (error) {
                console.error(`Error updating ${sequence} sequence:`, error);
                setLoadSequence(originalSequence as PickupData[]);
                alert(`Error updating ${sequence} sequence. Changes have been reverted.`);
            } finally {
                setIsEditLoading(false);
                setIsAnyItemEditing(false);
                setDraggedItemIndexLoad(null);
                setDraggedOverIndexLoad(null);
            }

        } else if (sequence === 'offload') {
            const deliverySequence = [...newSequence] as DeliveryData[];
            
            // Swap destination_id and offload_lane between the two items
            const tempDestinationId = deliverySequence[draggedIndex].destination_id;
            const tempOffloadLane = deliverySequence[draggedIndex].offload_lane;
            
            deliverySequence[draggedIndex].destination_id = deliverySequence[dropIndex].destination_id;
            deliverySequence[draggedIndex].offload_lane = deliverySequence[dropIndex].offload_lane;
            
            deliverySequence[dropIndex].destination_id = tempDestinationId;
            deliverySequence[dropIndex].offload_lane = tempOffloadLane;
            
            setOffloadSequence(deliverySequence);
            setIsAnyItemEditing(true);
            
            // Only send the two items that were swapped
            const editData = {
                parent_id: parentId,
                [dataKey]: [
                    {
                        id: deliverySequence[draggedIndex].id,
                        sequence: deliverySequence[draggedIndex].sequence,
                        location_id: deliverySequence[draggedIndex].destination_id,
                        secondary: deliverySequence[draggedIndex].offload_lane,
                    },
                    {
                        id: deliverySequence[dropIndex].id,
                        sequence: deliverySequence[dropIndex].sequence,
                        location_id: deliverySequence[dropIndex].destination_id,
                        secondary: deliverySequence[dropIndex].offload_lane,
                    }
                ]
            };

            try {
                setIsEditLoading(true);
                const success = await handleEditSequence(tableName, editData);

                if (!success) {
                    console.error(`API call failed for ${sequence} sequence, reverting to original sequence`);
                    setOffloadSequence(originalSequence as DeliveryData[]);
                    alert(`Failed to update ${sequence} sequence. Changes have been reverted.`);
                } else {
                    console.log(`${sequence} sequence updated successfully`);
                }
            } catch (error) {
                console.error(`Error updating ${sequence} sequence:`, error);
                setOffloadSequence(originalSequence as DeliveryData[]);
                alert(`Error updating ${sequence} sequence. Changes have been reverted.`);
            } finally {
                setIsEditLoading(false);
                setIsAnyItemEditing(false);
                setDraggedItemIndexUnload(null);
                setDraggedOverIndexUnload(null);
            }
        }
    };

    useEffect(() => {
        if (panelData && panelData.rowData.producer_id) {
            // get lease options filtered by producer_id
            const leaseOptions = getUniqueDropdownOptions({
                tableData: apiData?.lease_coordinates,
                valueColumn: 'id',
                labelColumn: 'lease',
                filterBy: { column: 'producer_id', value: panelData.rowData.producer_id }
            });
            setOriginDropdownOptions(leaseOptions);
        }
        
        const deliveryOptions = getUniqueDropdownOptions({
            tableData: apiData?.market_coordinates,
            valueColumn: 'id',
            labelColumn: 'station'
        });
        setDestinationDropdownOptions(deliveryOptions);        
        
    }, [apiData?.lease_coordinates, apiData?.market_coordinates, panelData]);

    // Handle add location button click
    const handleAddLocationClick = async () => {
        setIsAnyItemEditing(true);
        setIsEditLoading(true);

        const success = await handleAddRow('crude_schedule_pickup', {
            schedule_main_id: parentId,
            lease_id: parseInt(newOriginLocation),
            sequence: loadSequence.length + 1,
        });
        if (!success) {
            console.error(`Failed to add new pickup location.`);
        }
        setNewOriginLocation('');
        setIsEditLoading(false);
        setIsAnyItemEditing(false);
    };

    // Handle add destination button click
    const handleAddDestinationClick = async () => {
        setIsAnyItemEditing(true);
        setIsEditLoading(true);

        const success = await handleAddRow('crude_schedule_delivery', {
            schedule_main_id: parentId,
            destination_id: parseInt(newDestinationLocation),
            sequence: offloadSequence.length + 1,
        });
        if (!success) {
            console.error(`Failed to add new delivery location.`);
        }
        setNewDestinationLocation('');
        setIsEditLoading(false);
        setIsAnyItemEditing(false);
    };

    // Handle dropdown change for new origin location
    const handleAddOrigin = (value: string) => {
        setNewOriginLocation(value);
    };

    // Handle dropdown change for new origin location
    const handleAddDestination = (value: string) => {
        setNewDestinationLocation(value);
    };
    
    return (
        <div className="sequenceComponent-mainContainer">
            <div className="sequenceComponent-topContainer">
                <h2 className="sequenceComponent-sequenceTitle">Haul Sequence</h2>
                {isEditLoading &&(
                    <div className="sequenceComponent-loadingIcon">
                        <LoaderCircle 
                            size={16}
                            style={{ animation: 'spin 1s linear infinite' }}
                        />
                        editing...
                    </div>
                )}
            </div>
            {loadSequence.map((pickup, index) => {
                const maxPickupSeq = loadSequence.length ? loadSequence[loadSequence.length - 1].sequence : 0;
                const canDelete = pickup.sequence === maxPickupSeq && !pickup.ticket_number; // Only allow delete if it's the last pickup and doesn't have a ticket number
                return (
                    <div 
                        key={pickup.id}
                        className={`sequenceComponent-item ${
                            draggedItemIndexLoad === index ? 'sequenceComponent-item--dragging' : ''
                        } ${
                            draggedOverIndexLoad === index ? 'sequenceComponent-item--dragover' : ''
                        } ${
                            isAnyItemEditing && editedItemIndexLoad !== index ? 'sequenceComponent-item--editing-disabled' : ''
                        } ${
                            editedItemIndexLoad === index ? 'sequenceComponent-item--editing' : ''
                        }`}
                        draggable={!isAnyItemEditing}
                        onDragStart={(e) => handleDragStart(e, index, 'load')} // drag starts
                        onDragEnd={(e) => handleDragEnd(e, 'load')} // tracks when drag ends (for ui)
                        onDragOver={(e) => handleDragOver(e, index, 'load')} // dragging over other items (for ui)
                        onDragLeave={(e) => handleDragLeave('load')} // when leaving an item we were dragging over (for ui)
                        onDrop={(e) => handleDrop(e, index, 'load')} // actual drop, handles logic
                    > 
                        <SequenceItem
                            type="pickup"
                            parentId={parentId}
                            item_index={index}
                            data={pickup}
                            onEditStart={handleEditStart}
                            onLoadStart={handleLoadStart}
                            isAnyItemEditing={isAnyItemEditing}
                            dropdownOptions={originDropdownOptions}
                            canDelete={canDelete}
                        />
                    </div>
                )
            })}
            <div 
                className={`sequenceComponent-addLocationContainer${newOriginLocation ? ' sequenceComponent-addLocationContainer-enabled' : ''}`}
            >
                <button
                    className={`sequenceComponent-addLocation${newOriginLocation && !isAnyItemEditing ? ' sequenceComponent-addLocation-enabled' : ''}`}
                    onClick={handleAddLocationClick}
                    disabled={isAnyItemEditing || !newOriginLocation}
                >
                    <PlusIcon size={16} />
                </button>
                <div 
                    className={`sequenceComponent-addLocationContainer-dropdown${newOriginLocation ? ' sequenceComponent-addLocationContainer-dropdown-enabled' : ''}`}
                >
                    <Dropdown
                        options={originDropdownOptions}
                        placeholder="Add Pickup Location"
                        value={newOriginLocation}
                        onChange={handleAddOrigin}
                        height={26}
                        className='dropdown-inputMatch'
                        isDisabled={isAnyItemEditing}
                    />
                </div>
            </div>

            <div>
                <label className='sequenceComponent-middleDividerText'>
                    Pickup
                </label>
                <div className='sequenceComponent-middleDivider'/>
                <label className='sequenceComponent-middleDividerText'>
                    Offload
                </label>
            </div>

            {offloadSequence.map((delivery, index) => {
                const maxDeliverySeq = offloadSequence.length ? offloadSequence[offloadSequence.length - 1].sequence : 0;
                const canDelete = delivery.sequence === maxDeliverySeq;
                return (
                    <div 
                        key={delivery.id}
                        className={`sequenceComponent-item ${
                            draggedItemIndexUnload === index ? 'sequenceComponent-item--dragging' : ''
                        } ${
                            draggedOverIndexUnload === index ? 'sequenceComponent-item--dragover' : ''
                        } ${
                            isAnyItemEditing && editedItemIndexUnload !== index ? 'sequenceComponent-item--editing-disabled' : ''
                        }`}
                        draggable={!isAnyItemEditing}
                        onDragStart={(e) => handleDragStart(e, index, 'offload')} // drag starts
                        onDragEnd={(e) => handleDragEnd(e, 'offload')} // tracks when drag ends (for ui)
                        onDragOver={(e) => handleDragOver(e, index, 'offload')} // dragging over other items (for ui)
                        onDragLeave={(e) => handleDragLeave('offload')} // when leaving an item we were dragging over (for ui)
                        onDrop={(e) => handleDrop(e, index, 'offload')} // actual drop, handles logic
                    > 
                        <SequenceItem
                            type="delivery"
                            parentId={parentId}
                            totalVolume={totalVolume}
                            item_index={index}
                            data={delivery}
                            onEditStart={handleEditStart}
                            onLoadStart={handleLoadStart}
                            isAnyItemEditing={isAnyItemEditing}
                            dropdownOptions={destinationDropdownOptions}
                            canDelete={canDelete}
                        />
                    </div>
                )
            })}
            <div 
                className={`sequenceComponent-addLocationContainer${newDestinationLocation ? ' sequenceComponent-addLocationContainer-enabled' : ''}`}
            >
                <button
                    className={`sequenceComponent-addLocation${newDestinationLocation && !isAnyItemEditing ? ' sequenceComponent-addLocation-enabled' : ''}`}
                    onClick={handleAddDestinationClick}
                    disabled={isAnyItemEditing || !newDestinationLocation}
                >
                    <PlusIcon size={16} />
                </button>
                <div 
                    className={`sequenceComponent-addLocationContainer-dropdown${newDestinationLocation ? ' sequenceComponent-addLocationContainer-dropdown-enabled' : ''}`}
                >
                    <Dropdown
                        options={destinationDropdownOptions}
                        placeholder="Add Drop-off Location"
                        value={newDestinationLocation}
                        onChange={handleAddDestination}
                        height={26}
                        className='dropdown-inputMatch'
                        isDisabled={isAnyItemEditing}
                    />
                </div>
            </div>
            <div className='sequenceComponent-middleDivider'/>
            <label className='sequenceComponent-mileageText'>
                {panelData?.rowData.total_mileage ? 'Total mileage - ' + panelData?.rowData.total_mileage : 'No mileage data'}
            </label>
        </div>
    );
};

export default SequenceComponent;